import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Leaf, Check, XCircle, Phone, Mail, Info, ExternalLink, Droplets, MessageCircle, AlertTriangle, FileText } from 'lucide-react';
import { standardServiceScope, cleaningProducts } from '../utils/estimateData';
import './ScopeOfService.css';

const SERVICE_TABS = [
  { key: 'recurring', label: 'Recurring' },
  { key: 'deep_clean', label: 'Deep Clean' },
  { key: 'move_in_out', label: 'Move In/Out' },
  { key: 'products', label: 'Our Products' },
];

function ScopeOfService() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('service') || 'recurring';
  const validTab = standardServiceScope[initialTab] || initialTab === 'products' ? initialTab : 'recurring';
  const [activeTab, setActiveTab] = useState(validTab);

  const isProductsTab = activeTab === 'products';
  const scopeData = !isProductsTab ? standardServiceScope[activeTab] : null;

  return (
    <div className="scope-page">
      <div className="scope-page-container">
        {/* Header */}
        <div className="scope-page-header">
          <div className="scope-page-logo">
            <div className="scope-page-logo-icon"><Leaf size={24} /></div>
            <div>
              <h1>Two Sisters</h1>
              <span>Cleaning & Organizing</span>
            </div>
          </div>
        </div>

        {/* Content Card */}
        <div className="scope-page-card">
          {/* Title */}
          <div className="scope-page-title-section">
            <h2>{isProductsTab ? 'Products We Use' : 'Standard Scope of Service'}</h2>
            <p>
              {isProductsTab
                ? 'We carefully select products that are effective, safe, and leave your home smelling fresh — never harsh or chemical-heavy.'
                : 'See exactly what\'s included in each of our cleaning services. Every home is unique — your personalized quote may include adjustments based on your specific needs.'
              }
            </p>
          </div>

          {/* Service Tabs */}
          <div className="scope-tabs">
            {SERVICE_TABS.map(tab => (
              <button
                key={tab.key}
                className={`scope-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Products Tab */}
          {isProductsTab && (
            <div className="scope-page-service">
              <div className="scope-page-note">
                <Droplets size={16} />
                <span><strong>Transparency matters to us.</strong> Below is the full list of products our team uses. If you have allergies, sensitivities, or product preferences, just let us know — we're happy to accommodate.</span>
              </div>

              {cleaningProducts.map((group, gIdx) => (
                <div key={gIdx} className="scope-page-room">
                  <h4><span className="scope-page-room-icon">{group.icon}</span> {group.category}</h4>
                  <ul className="products-list">
                    {group.items.map((product, pIdx) => (
                      <li key={pIdx} className="product-item">
                        <Check size={14} className="check-icon" />
                        <div>
                          <strong>{product.name}</strong>
                          <span className="product-desc">{product.description}</span>
                          {product.link && (
                            <a href={product.link} target="_blank" rel="noopener noreferrer" className="product-link">
                              <ExternalLink size={12} /> View Product
                            </a>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <div className="scope-page-products-note">
                <p>Have a product preference or sensitivity? We're happy to use a specific product you provide or adjust our lineup for your home.</p>
              </div>
            </div>
          )}

          {/* Service Scope Tabs */}
          {!isProductsTab && scopeData && (
            <div className="scope-page-service">
              <div className="scope-page-service-header">
                <h3>{scopeData.label}</h3>
                <span>{scopeData.subtitle}</span>
              </div>

              {/* Variance Note */}
              <div className="scope-page-note">
                <Info size={16} />
                <span><strong>Every estimate can vary.</strong> The items below represent our standard scope. When you receive a personalized quote, any additions or exclusions specific to your home will be clearly listed.</span>
              </div>

              {/* Rooms */}
              {Object.entries(scopeData.rooms).map(([key, room]) => (
                <div key={key} className="scope-page-room">
                  <h4><span className="scope-page-room-icon">{room.icon}</span> {room.label}</h4>
                  <ul>
                    {room.items.map((item, idx) => {
                      const isObject = typeof item === 'object';
                      const text = isObject ? item.text : item;
                      const warning = isObject ? item.warning : null;
                      return (
                        <li key={idx} className={text.includes('PLUS:') ? 'highlight' : ''}>
                          {!text.includes('PLUS:') && <Check size={14} className="check-icon" />}
                          <span>{text}</span>
                          {warning && (
                            <div className="scope-item-warning">
                              <AlertTriangle size={14} />
                              <span>{warning}</span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}

              {/* Not Included */}
              {scopeData.notIncluded && scopeData.notIncluded.length > 0 && (
                <div className="scope-page-excluded">
                  <h4><XCircle size={16} /> Not Included in Standard Scope</h4>
                  <ul>
                    {scopeData.notIncluded.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* CTA */}
          <div className="scope-page-cta">
            <p>Ready for a personalized quote?</p>
            <a href="/quote" className="scope-page-cta-btn">Request a Free Estimate</a>
          </div>

          {/* Feedback Widget */}
          <div className="scope-page-feedback">
            <div className="scope-page-feedback-icon">
              <MessageCircle size={22} />
            </div>
            <h4>Did we miss something?</h4>
            <p>We're not AI — we're human, and we make mistakes. If you notice something within 24 hours of your clean, please let us know and we'll come back to fix the mistake and make it right.</p>
            <a href="tel:+14342072246" className="scope-page-feedback-btn">
              <Phone size={15} /> Let Us Know
            </a>
          </div>

          {/* Terms & Conditions Link */}
          <div className="scope-page-terms">
            <a href="/terms"><FileText size={14} /> View our Terms & Conditions</a>
          </div>

          {/* Footer */}
          <div className="scope-page-footer">
            <p>Questions about what's included?</p>
            <div className="scope-page-contact">
              <a href="tel:+14342072246"><Phone size={16} /> (434) 207-2246</a>
              <a href="mailto:hello@twosisterscleaningco.com"><Mail size={16} /> hello@twosisterscleaningco.com</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScopeOfService;
